import { useEffect, useState } from "react";
import "./permission.scss";
import { Button, Modal, Space, Table } from "antd";
import { RoleServices } from "../../services/role";
import { IRoleResult, IRolesResponse } from "../../interfaces/role";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useMessageApi } from "../../context/MessageContext";
import { PERMISSIONS } from "../../constants/permission";
import { useHasPermission } from "../../hooks/useHasPermission";

interface ListRoleProps {
  onEditRole: (role: IRoleResult) => void;
  trigger: boolean;
  onDoneTrigger: () => void;
}

const ListRole: React.FC<ListRoleProps> = ({
  onEditRole,
  trigger,
  onDoneTrigger,
}) => {
  const messageApi = useMessageApi();
  const [roles, setRoles] = useState<IRoleResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectRole, setSelectRole] = useState<IRoleResult>();
  const hasPermissionEditor = useHasPermission([PERMISSIONS.PERMISSION_EDITOR]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response: IRolesResponse = await RoleServices.getAll();
      if (response.success) {
        const sortedData = response.data.results.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setRoles(sortedData);
      }
    } catch (error) {
      console.error("Fetch roles error:", error);
    } finally {
      setLoading(false);
      onDoneTrigger();
    }
  };

  useEffect(() => {
    if (trigger) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  const columns = [
    {
      title: "Role",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Number of Users",
      dataIndex: "numberOfUsers",
      key: "numberOfUsers",
    },
    {
      title: "Permission",
      key: "permissions",
      render: (_: any, record: IRoleResult) => renderPermissionField(_, record),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      render: (_: any, record: IRoleResult) =>
        hasPermissionEditor ? (
          <Space size="small" className="box-action" align="center">
            <Button type="link"
              disabled={!!record.isRoleAdmin}
              onClick={() => onEditRole(record)}>
              <FontAwesomeIcon icon={faPen} />
            </Button>
            <div>|</div>
            <Button
              type="link"
              disabled={record.numberOfUsers !== 0 || !!record.isRoleAdmin}
              data-testid="open-delete-role"
              onClick={() => showModalDeleteRole(record)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Space>
        ) : null,
    },
  ];

  const renderPermissionField = (_: any, record: IRoleResult) => {
    const {
      permissionsEditor = [],
      permissionsViewer = [],
      fullAccess,
    } = record;
    if (fullAccess) {
      return <strong>Full access</strong>;
    }

    return (
      <>
        <div>
          {permissionsEditor.length > 0 && (
            <>
              <strong>Editor: </strong> {permissionsEditor.join(", ")}
            </>
          )}
        </div>
        <div>
          {permissionsViewer.length > 0 && (
            <>
              <strong>Viewer: </strong> {permissionsViewer.join(", ")}
            </>
          )}
        </div>
      </>
    );
  };

  const showModalDeleteRole = (role: IRoleResult) => {
    if (!role?.numberOfUsers) {
      setSelectRole(role);
      setIsModalOpen(true);
    }
  };

  const handleCancelDeleteRole = () => {
    setIsModalOpen(false);
  };

  const handleDeleteRole = async () => {
    if (!selectRole?.id) return;
    setIsLoadingDelete(true);
    try {
      const res: any = await RoleServices.deleteRole(
        selectRole?.id?.toString()
      );
      if (res?.success) {
        messageApi.success(res?.message || "");
        setIsModalOpen(false);
        fetchData();
      }
    } catch (error: any) {
      messageApi.error(error?.message);
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const renderModalDeleteRole = () => (
    <Modal
      title="Confirm delete"
      closable={{ "aria-label": "Custom Close Button" }}
      open={isModalOpen}
      onOk={handleDeleteRole}
      onCancel={handleCancelDeleteRole}
      okText="Delete"
      confirmLoading={isLoadingDelete}
    >
      <p style={{ fontSize: "20px" }}>
        Are you sure you want to delete role{" "}
        <span className="text-primary font-italic">{selectRole?.name}</span>?{" "}
        <br />
        This action cannot be undone.
      </p>
    </Modal>
  );

  return (
    <>
      <Table
        dataSource={roles}
        loading={loading}
        rowKey={(record) => record.id}
        columns={columns}
        pagination={false}
        size="small"
        className="table-fm"
        rowClassName={(record, index) =>
          index % 2 === 0 ? "highlight-row" : ""
        }
      />
      {renderModalDeleteRole()}
    </>
  );
};

export default ListRole;
